import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { VIRAL_ECHO_PLUS_IN_CIRCLE } from '../config/viralEchoHealEffect';
import './ViralEchoHealPlusEffect.css';

/**
 * Picks the largest visible [data-zone] node (mirrors slot VFX: mobile + desktop duplicates).
 */
function getGeneralZoneRect(dataZone) {
  if (typeof document === 'undefined') return null;
  const candidates = document.querySelectorAll(`[data-zone="${dataZone}"]`);
  let bestEl = null;
  let bestArea = -1;
  candidates.forEach((el) => {
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return;
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return;
    const area = r.width * r.height;
    if (area > bestArea) {
      bestArea = area;
      bestEl = el;
    }
  });
  if (!bestEl) return null;
  return bestEl.getBoundingClientRect();
}

/** Pixel centers for each + inside the circular profile (inscribed in rect). */
function plusPositionsInProfile(rect) {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const halfMin = Math.min(rect.width, rect.height) / 2;

  return VIRAL_ECHO_PLUS_IN_CIRCLE.map(({ angleDeg, radiusFrac }) => {
    const dist = halfMin * radiusFrac * 2;
    const rad = (angleDeg * Math.PI) / 180;
    return {
      x: cx + dist * Math.cos(rad),
      y: cy + dist * Math.sin(rad),
    };
  });
}

/**
 * Three marks in quick succession inside the general portrait: green + (heal) or red × (damage).
 */
export function ViralEchoHealPlusEffect({ generalDataZone, animationKey, variant = 'heal' }) {
  const [points, setPoints] = useState(null);

  useLayoutEffect(() => {
    let cancelled = false;
    const measure = (attempt) => {
      if (!generalDataZone) {
        if (!cancelled) setPoints(null);
        return;
      }
      const rect = getGeneralZoneRect(generalDataZone);
      if (rect) {
        if (!cancelled) setPoints(plusPositionsInProfile(rect));
        return;
      }
      if (attempt < 24 && !cancelled) {
        requestAnimationFrame(() => {
          if (!cancelled) measure(attempt + 1);
        });
      }
    };
    setPoints(null);
    measure(0);
    return () => {
      cancelled = true;
    };
  }, [generalDataZone, animationKey]);

  if (!points || points.length === 0) {
    return null;
  }

  const layer = (
    <div
      className="viral-echo-heal-layer"
      aria-hidden
      data-animation-key={animationKey}
    >
      {points.map((pt, i) => (
        <div
          key={i}
          className="viral-echo-heal-mark"
          style={{
            left: `${pt.x}px`,
            top: `${pt.y}px`,
          }}
        >
          <span
            className={
              variant === 'damage'
                ? `viral-echo-damage-x viral-echo-damage-x--${i}`
                : `viral-echo-heal-plus viral-echo-heal-plus--${i}`
            }
          >
            {variant === 'damage' ? '×' : '+'}
          </span>
        </div>
      ))}
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(layer, document.body);
  }
  return layer;
}

export default ViralEchoHealPlusEffect;
